import { Recurrence, RecurrenceType, StaticInitializers } from '@tableau/self-service-schedules';
import { DailyRecurrence, DailyRecurrenceDetails, HourlyRecurrence, HourlyRecurrenceDetails, MonthlyRecurrence, MonthlyRecurrenceDetails, RecurrenceConverters, RecurrenceDaysOfTheWeek, RecurrenceMode, WeeklyRecurrence, WeeklyRecurrenceDetails } from '@tableau/self-service-schedules/lib/src/components/Recurrence';

import { ScheduleTypes } from '../types/ISchedule';
import { FrequencyDetails, Interval, IntervalHoursType, IntervalMonthDayType, IntervalWeekDayType, SelfServiceSchedule, WeekDay, WeekDayOccurrence } from '../types/SelfServiceSchedule';

/**
 * Util functions for self service schedules with frequencies and utilities
 * Based on ScheduleHelpers.java in the monolith
 * User docs example: extract refresh schedules on Cloud:
 * https://help.tableau.com/current/api/rest_api/en-us/REST/rest_api_ref.htm#create_cloud_extract_refresh_task
 */
export class SelfServiceScheduleUtils {

  private static recurrenceDayOfMonthToOccurrence: Map<string, WeekDayOccurrence> = new Map([
    ['0', WeekDayOccurrence.LastDay],
    ['1', WeekDayOccurrence.First],
    ['2', WeekDayOccurrence.Second],
    ['4', WeekDayOccurrence.Third],
    ['8', WeekDayOccurrence.Fourth],
    ['16', WeekDayOccurrence.Fifth]
  ]);

  /**
   * convert a Self Service Schedule from Recurrence
   */
  public static getSSScheduleFromRecurrence(recurrence: Recurrence): SelfServiceSchedule {
    switch (recurrence.recurrenceType) {
      case RecurrenceType.Hourly:
        return this.getRestApiHourlySchedule(recurrence.recurrenceDetails);
      case RecurrenceType.Daily:
        return this.getRestApiDailySchedule(recurrence.recurrenceDetails);
      case RecurrenceType.Weekly:
        return this.getRestApiWeeklySchedule(recurrence.recurrenceDetails);
      case RecurrenceType.Monthly:
        return this.getRestApiMonthlySchedule(recurrence.recurrenceDetails);
    }
  }

  public static getRecurrenceFromSSSchedule(schedule?: SelfServiceSchedule): Recurrence {
    if (!schedule || !schedule.frequency) {
      return StaticInitializers.defaultRecurrence();
    }
    try {
      switch (schedule.frequency) {
        case ScheduleTypes.Hourly:
            return this.getHourlyRecurrence(schedule.frequencyDetails);
        case ScheduleTypes.Daily:
          return this.getDailyRecurrence(schedule.frequencyDetails);
        case ScheduleTypes.Weekly:
          return this.getWeeklyRecurrence(schedule.frequencyDetails);
        case ScheduleTypes.Monthly:
          return this.getMonthlyRecurrence(schedule.frequencyDetails);
      }
    } catch (e) {
      // should never be hit
      return StaticInitializers.defaultRecurrence();
    }
  }

  private static getRestApiHourlySchedule(recurrence: HourlyRecurrenceDetails): SelfServiceSchedule {
    return {
      frequency: ScheduleTypes.Hourly,
      frequencyDetails: {
        start: this.convertMinsPastMidnightToTime(recurrence.startTime),
        end: this.convertMinsPastMidnightToTime(recurrence.endTime),
        intervals: {
          interval: [
            this.getHourInterval(recurrence.recurrenceMinutes),
            ...this.getWeekDayIntervals(recurrence.recurrenceDaysOfWeek)
          ]
        }
      }
    };
  }

  private static getHourlyRecurrence(frequencyDetails: FrequencyDetails): HourlyRecurrence {
    return {
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: RecurrenceType.Hourly,
      recurrenceDetails: {
        startTime: this.convertTimeToMinsPastMidnight(frequencyDetails.start),
        endTime: this.convertTimeToMinsPastMidnight(frequencyDetails.end),
        recurrenceDaysOfWeek: this.getRecurrenceDaysOfWeek(frequencyDetails.intervals.interval),
        recurrenceMinutes: 60
      }
    };
  }

  private static getRestApiDailySchedule(recurrence: DailyRecurrenceDetails): SelfServiceSchedule {
    return {
      frequency: ScheduleTypes.Daily,
      frequencyDetails: {
        start: this.convertMinsPastMidnightToTime(recurrence.startTime),
        end: this.convertMinsPastMidnightToTime(recurrence.endTime),
        intervals: {
          interval: [
            this.getHourInterval(recurrence.recurrenceMinutes),
            ...this.getWeekDayIntervals(recurrence.recurrenceDaysOfWeek)
          ]
        }
      }
    };
  }

  private static getDailyRecurrence(frequencyDetails: FrequencyDetails): DailyRecurrence {
    const startTime = this.convertTimeToMinsPastMidnight(frequencyDetails.start);
    return {
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: RecurrenceType.Daily,
      recurrenceDetails: {
        startTime,
        endTime: frequencyDetails.end ? this.convertTimeToMinsPastMidnight(frequencyDetails.end) : startTime,
        recurrenceDaysOfWeek: this.getRecurrenceDaysOfWeek(frequencyDetails.intervals.interval),
        recurrenceMinutes: this.getRecurrenceMinutes(frequencyDetails.intervals.interval)
      }
    };
  }

  private static getRestApiWeeklySchedule(recurrence: WeeklyRecurrenceDetails): SelfServiceSchedule {
    return {
      frequency: ScheduleTypes.Weekly,
      frequencyDetails: {
        start: this.convertMinsPastMidnightToTime(recurrence.startTime),
        intervals: {
          interval: this.getWeekDayIntervals(recurrence.recurrenceDaysOfWeek)
        }
      }
    };
  }

  private static getWeeklyRecurrence(frequencyDetails: FrequencyDetails): WeeklyRecurrence {
    return {
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: RecurrenceType.Weekly,
      recurrenceDetails: {
        startTime: this.convertTimeToMinsPastMidnight(frequencyDetails.start),
        recurrenceDaysOfWeek: this.getRecurrenceDaysOfWeek(frequencyDetails.intervals.interval),
      }
    };
  }

  private static getRestApiMonthlySchedule(recurrence: MonthlyRecurrenceDetails): SelfServiceSchedule {
    return {
      frequency: ScheduleTypes.Monthly,
      frequencyDetails: {
        start: this.convertMinsPastMidnightToTime(recurrence.startTime),
        intervals: {
          interval: this.getMonthDayIntervals(recurrence)
        }
      }
    };
  }

  private static getMonthlyRecurrence(frequencyDetails: FrequencyDetails): MonthlyRecurrence {
    return {
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: RecurrenceType.Monthly,
      recurrenceDetails: this.getMonthlyRecurrenceDetails(frequencyDetails)
    };
  }

  /**
   * convert from recurrence mins past midnight to HH:MM:SS
   */
  private static convertMinsPastMidnightToTime(minsPastMidnight: number): string {
    const mins: number = minsPastMidnight % 60;
    const mm: string = mins < 10 ? `0${mins}` : mins.toString();
    const hours: number = (minsPastMidnight - mins) / 60;
    const hh: string = hours < 10 ? `0${hours}` : hours.toString();
    return `${hh}:${mm}:00`;
  }

  /**
   * convert from HH:MM:SS to mins past midnight for recurrences
   */
  private static convertTimeToMinsPastMidnight(hhmmss: string): number {
    const timeUnits = hhmmss.split(':');
    const hours = parseInt(timeUnits[0]);
    const minutes = parseInt(timeUnits[1]);
    return hours * 60 + minutes;
  }

  private static getHourInterval(mins: number): IntervalHoursType {
    return ({
      hours: mins / 60
    }) as IntervalHoursType;
  }

  private static getRecurrenceMinutes(intervals: Interval[]): number {
    const hourInterval = intervals.find(interval => interval.hours);
    return hourInterval.hours * 60;
  }

  private static getWeekDayIntervals(recurrenceDaysOfWeek: RecurrenceDaysOfTheWeek[]): IntervalWeekDayType[] {
    return recurrenceDaysOfWeek.map(recurrenceDay => ({
      weekDay: this.getWeekDay(recurrenceDay)
    }));
  }

  private static getWeekDay(recurrenceDaysOfWeek: RecurrenceDaysOfTheWeek): WeekDay {
    switch (recurrenceDaysOfWeek) {
      case RecurrenceDaysOfTheWeek.Mon:
        return WeekDay.Monday;
      case RecurrenceDaysOfTheWeek.Tue:
        return WeekDay.Tuesday;
      case RecurrenceDaysOfTheWeek.Wed:
        return WeekDay.Wednesday;
      case RecurrenceDaysOfTheWeek.Thu:
        return WeekDay.Thursday;
      case RecurrenceDaysOfTheWeek.Fri:
        return WeekDay.Friday;
      case RecurrenceDaysOfTheWeek.Sat:
        return WeekDay.Saturday;
      case RecurrenceDaysOfTheWeek.Sun:
        return WeekDay.Sunday;
    }
  }

  private static getRecurrenceDaysOfWeek(intervals: Interval[]): RecurrenceDaysOfTheWeek[] {
    const weeklyIntervals = intervals.filter(interval => interval.weekDay);
    return weeklyIntervals.map(interval => this.getRecurrenceDayOfWeek(interval.weekDay));
  }

  private static getRecurrenceDayOfWeek(weekDay: WeekDay): RecurrenceDaysOfTheWeek {
    switch (weekDay) {
      case WeekDay.Monday:
        return RecurrenceDaysOfTheWeek.Mon;
      case WeekDay.Tuesday:
        return RecurrenceDaysOfTheWeek.Tue;
      case WeekDay.Wednesday:
        return RecurrenceDaysOfTheWeek.Wed;
      case WeekDay.Thursday:
        return RecurrenceDaysOfTheWeek.Thu;
      case WeekDay.Friday:
        return RecurrenceDaysOfTheWeek.Fri;
      case WeekDay.Saturday:
        return RecurrenceDaysOfTheWeek.Sat;
      case WeekDay.Sunday:
        return RecurrenceDaysOfTheWeek.Sun;
    }
  }

  private static getMonthDayIntervals(recurrence: MonthlyRecurrenceDetails): IntervalMonthDayType[] {
    // if there is no weekday, then 'Day' must have been selected in the 'On' dropdown
    if (!recurrence.recurrenceDaysOfWeek || recurrence.recurrenceDaysOfWeek.length === 0) {
      // last day of the month
      if (recurrence.recurrenceDayOfMonth === '0') {
        return [{
          monthDay: WeekDayOccurrence.LastDay
        }];
      }

      // if there is no day of the week and it is not the last day of the month, then individual days of the month set
      const daysOfMonth: number[] = RecurrenceConverters.toDaysOfMonthArr(recurrence.recurrenceDayOfMonth);
      return daysOfMonth.map(day => ({
        monthDay: day
      }));
    }

    // there is at max one weekday
    const weekDay = this.getWeekDay(recurrence.recurrenceDaysOfWeek[0]);
    const weekDayOccurrence = this.getWeekDayOccurrence(recurrence.recurrenceDayOfMonth);
    return [{
      monthDay: weekDayOccurrence,
      weekDay
    }];
  }

  private static getWeekDayOccurrence(recurrenceDayOfMonth: string): WeekDayOccurrence {
    return this.recurrenceDayOfMonthToOccurrence.get(recurrenceDayOfMonth);
  }

  private static getRecurrenceDayOfMonth(weeklyOccurrence: WeekDayOccurrence): string {
    for (let [key, value] of Array.from(this.recurrenceDayOfMonthToOccurrence.entries())) {
      if (value === weeklyOccurrence)
          return key;
    }
    // should never be reached
    throw new TypeError('unable to convert RestApiWeekDayOccurrence to MonthDay');
  }

  private static getMonthlyRecurrenceDetails(frequencyDetails: FrequencyDetails): MonthlyRecurrenceDetails {
    const startTime = this.convertTimeToMinsPastMidnight(frequencyDetails.start);
    const monthDayIntervals = frequencyDetails.intervals.interval.filter(interval => interval.monthDay);
    const firstMonthDayInterval = monthDayIntervals[0];
    if (firstMonthDayInterval.weekDay) {
      const recurrenceDaysOfWeek = this.getRecurrenceDaysOfWeek([firstMonthDayInterval]);
      const recurrenceDayOfMonth = this.getRecurrenceDayOfMonth(firstMonthDayInterval.monthDay as WeekDayOccurrence);
      return {
        startTime,
        recurrenceDaysOfWeek,
        recurrenceDayOfMonth,
      };
    }

    // last day of month
    if (firstMonthDayInterval.monthDay === WeekDayOccurrence.LastDay) {
      return {
        startTime,
        recurrenceDayOfMonth: '0',
        recurrenceDaysOfWeek: [],
      };
    }

    // days selected
    const selectedDays: number[] = monthDayIntervals.map(interval => interval.monthDay as number);
    return{
      startTime,
      recurrenceDayOfMonth: RecurrenceConverters.toDaysOfMonthMask(selectedDays).toString(),
      recurrenceDaysOfWeek: [],
    };
  }
}